import React, { useState } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import validator from 'validator'
import styled from 'styled-components'
import ScreenWrapper from '../shared/ScreenWrapper'
import FeatureTableRow from '../shared/FeatureTableRow'
import Text from '../shared/Text'
import Button from '../shared/Button'
import StoreButton from '../shared/StoreButton'
import styles from '../../utility/styles'
import { ProIconGold } from '../../utility/icons'
import ProofStrip from '../shared/ProofStrip'
import FormInput from '../shared/FormInput'

const StyledContainer = styled(Container)`
	margin-top: 72px;
	margin-bottom: 72px;
`

const TopContainer = styled(Container)`
	margin-top: 56px;
	margin-bottom: 56px;
	@media only screen and (max-width: 992px) {
		margin-top: 32px;
	}
`

const Eyebrow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 16px;
`

const PricePanel = styled.div`
	margin-top: 32px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
`

const Toggle = styled.div`
	display: inline-flex;
	background: var(--tz-wash, rgba(45, 54, 70, 0.4));
	border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.08));
	border-radius: 8px;
	padding: 3px;
	gap: 2px;
`

const ToggleButton = styled.button`
	border: none;
	border-radius: 6px;
	padding: 7px 16px;
	font-family: ${styles.fonts.geist};
	font-weight: 500;
	font-size: 13px;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 8px;
	transition: all 0.15s;
	background: ${({ active }) => (active ? 'var(--tz-hairline-strong, rgba(255, 255, 255, 0.12))' : 'transparent')};
	color: ${({ active }) => (active ? styles.colors.white : styles.colors.mediumGray)};
	&:hover {
		color: ${styles.colors.white};
	}
`

const ValueTag = styled.span`
	font-family: ${styles.fonts.geistMono};
	font-size: 10px;
	line-height: 16px;
	font-weight: 500;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: ${styles.colors.founders};
`

const PriceRow = styled.div`
	display: flex;
	align-items: baseline;
	gap: 8px;
	margin: 20px 0 4px 0;
`

const InputWrapper = styled.div`
	display: flex;
	flex-direction: column;
	padding: 28px;
	border-radius: ${({ theme }) => theme.radiusLg};
	background-color: ${({ theme }) => theme.surface};
	border: 1px solid ${({ theme }) => theme.border};
	@media only screen and (max-width: 991px) {
		margin: 16px 0;
	}
`

const StoreRow = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 12px;
`

const CenteredDiv = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`

const StyledForm = styled.form`
	width: 100%;
	margin: 16px 0 4px 0;
`

const ValuePanel = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	background-color: ${styles.colors.darkBackground};
	border: 1px solid var(--tz-hairline, rgba(45, 54, 70, 0.6));
	border-radius: 12px;
	overflow: hidden;
	@media only screen and ${styles.breakpoints.tablet} {
		grid-template-columns: 1fr;
	}
`

const ValueCell = styled.div`
	padding: 28px 32px;
	& + & {
		border-left: 1px solid var(--tz-hairline-strong, rgba(120, 126, 136, 0.3));
	}
	@media only screen and ${styles.breakpoints.tablet} {
		& + & {
			border-left: none;
			border-top: 1px solid var(--tz-hairline-strong, rgba(120, 126, 136, 0.3));
		}
	}
`

const BottonGiftContainer = styled.div`
	display: flex;
	align-items: center;
	padding: 16px 24px 16px 0;
	background-color: ${styles.colors.darkBackground};
	border: 1px solid var(--tz-hairline, rgba(45, 54, 70, 0.6));
	border-radius: 8px;
	margin: 0 auto;
	@media only screen and ${styles.breakpoints.mobileL} {
		flex-direction: column;
		align-items: flex-start;
		padding: 0 24px 16px 24px;
	}
`

const BotomGiftIconContainer = styled.div`
	padding: 24px;
	background-color: ${styles.colors.founders};
	border-radius: 0 8px 8px 0;
	@media only screen and ${styles.breakpoints.mobileL} {
		border-radius: 0 0 8px 8px;
	}
`

const BottomGiftTextContainer = styled.div`
	margin: 0 24px;
	@media only screen and ${styles.breakpoints.mobileL} {
		margin: 24px 0;
	}
`

const VALUE_BLOCKS = [
	{
		title: 'Every EV in one place',
		body: 'Track every vehicle you own with add-on connections and see your whole garage at a glance.',
	},
	{
		title: 'History that never expires',
		body: 'Keep every drive, charge, and road trip with unlimited data history and CSV export.',
	},
	{
		title: 'Know your battery & FSD',
		body: 'Go deep on battery health, charging efficiency, and FSD/Autopilot analysis.',
	},
]

const onSubmit = e => e.preventDefault()

export default function Subscribe({ pricing = {}, ratings = {} }) {
	const { upgrade_features, monthly, annual, savings_percent } = pricing
	const [email, setEmail] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [success, setSuccess] = useState(false)
	const [error, setError] = useState('')
	const [billing, setBilling] = useState('annual')

	const onChange = ({ target }) => setEmail(target.value)

	const onSubmitEmail = async () => {
		if (isSubmitting) return
		try {
			if (!validator.isEmail(email)) throw 'invalid email'
			setIsSubmitting(true)
			setError('')
			const response = await fetch('/v2/password/magic_link', {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ email: email }),
			})

			if (response.status !== 200) throw 'unable to send'
			const parsed_response = await response.json()
			setSuccess(parsed_response.request_processed)
			setIsSubmitting(false)
		} catch (error) {
			setError(
				'Hmm, something went wrong. If this keeps happening, please give us a shout at support@tezlabapp.com so we can help.'
			)
			setSuccess(false)
			setIsSubmitting(false)
		}
	}

	const goToInput = () => {
		document.getElementById('input')?.scrollIntoView({ behavior: 'smooth' })
		document.getElementById('email')?.focus({ preventScroll: true })
	}

	React.useEffect(() => {
		if (window.location.hash === '#input') {
			document.getElementById('input')?.scrollIntoView()
			document.getElementById('email')?.focus()
		}
	}, [])

	return (
		<ScreenWrapper>
			<TopContainer>
				<Row>
					<Col xs={12} md={6}>
						<Eyebrow>
							<ProIconGold width='40' height='40' />
							<Text.Caption color={styles.colors.founders} style={{ fontFamily: styles.fonts.geistMono, fontSize: '12px', fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase' }}>TezLab Pro</Text.Caption>
						</Eyebrow>
						<Text.Huge className='mb-3' type='mega' mobileL='h2' variant='bold' color={styles.colors.white}>Get more out of every mile</Text.Huge>
						<Text.Heading type='h5'>Unlock unlimited history, deep battery and charging insight, and FSD tracking. Add extra vehicles and enhanced FSD analysis whenever you need them.</Text.Heading>

						<PricePanel>
							<Toggle role='group' aria-label='Billing interval'>
								<ToggleButton type='button' aria-pressed={billing === 'annual'} active={billing === 'annual'} onClick={() => setBilling('annual')}>
									Annual
									<ValueTag active={billing === 'annual'}>{savings_percent ? `Save ${savings_percent}%` : 'Best value'}</ValueTag>
								</ToggleButton>
								<ToggleButton type='button' aria-pressed={billing === 'monthly'} active={billing === 'monthly'} onClick={() => setBilling('monthly')}>
									Monthly
								</ToggleButton>
							</Toggle>
							<PriceRow>
								<Text.Heading type='h1' mobileL='h2' variant='bold' color={styles.colors.founders} style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{billing === 'annual' ? annual : monthly}</Text.Heading>
								<Text.Heading type='h5' color={styles.colors.lightGray}>{billing === 'annual' ? '/ year' : '/ month'}</Text.Heading>
							</PriceRow>
							<Text.Caption color={styles.colors.lightGray}>All first-time subscriptions start with a full-access 14-day free trial. Includes 1 vehicle connection, with more available as add-ons.</Text.Caption>
							<Button onClick={goToInput} style={{ marginTop: '20px', alignSelf: 'flex-start', backgroundColor: styles.colors.founders, color: styles.colors.black }}>Get Started</Button>
						</PricePanel>
					</Col>
					<Col xs={12} md={{ span: 5, offset: 1 }}>
						<InputWrapper id='input' style={{ scrollMarginTop: '88px' }}>
							{success ? (
								<svg
									width='73'
									height='56'
									viewBox='0 0 73 56'
									fill='none'
									xmlns='http://www.w3.org/2000/svg'
									style={{ marginBottom: '12px' }}
								>
									<mask
										id='mask0_2377_51953'
										style={{ maskType: 'alpha' }}
										maskUnits='userSpaceOnUse'
										x='0'
										y='3'
										width='70'
										height='53'
									>
										<path
											fillRule='evenodd'
											clipRule='evenodd'
											d='M45.8338 11.8788C45.8338 8.73919 46.7811 5.82086 48.4056 3.39404H0.015625V56.0001H69.5914V24.5797C67.1646 26.2041 64.2462 27.1515 61.1065 27.1515C52.6716 27.1515 45.8338 20.3137 45.8338 11.8788Z'
											fill='#10ABFF'
										/>
									</mask>
									<g mask='url(#mask0_2377_51953)'>
										<path
											d='M4.25781 16.8618C4.25781 12.4435 7.83953 8.86182 12.2578 8.86182H57.3487C61.767 8.86182 65.3487 12.4435 65.3487 16.8618V41.5891C65.3487 46.0074 61.767 49.5891 57.3487 49.5891H12.2578C7.83953 49.5891 4.25781 46.0074 4.25781 41.5891V16.8618Z'
											stroke='#10ABFF'
											strokeWidth='3'
											strokeLinejoin='round'
										/>
										<path
											d='M6.80469 47.0907L26.0771 28.5232M63.6532 47.0907L43.5316 28.5232'
											stroke='#10ABFF'
											strokeWidth='3'
											strokeLinecap='round'
										/>
										<path
											d='M6.38281 11.0303L30.8017 32.6943C33.0835 34.7187 36.5211 34.7088 38.7912 32.6714L61.9586 11.8788'
											stroke='#10ABFF'
											strokeWidth='3'
											strokeLinecap='round'
										/>
									</g>
									<path
										fillRule='evenodd'
										clipRule='evenodd'
										d='M61.1054 0C54.5449 0 49.2266 5.31832 49.2266 11.8788C49.2266 18.4393 54.5449 23.7576 61.1054 23.7576C67.6658 23.7576 72.9841 18.4393 72.9841 11.8788C72.9841 5.31832 67.6658 0 61.1054 0Z'
										fill='#10ABFF'
									/>
									<path
										fillRule='evenodd'
										clipRule='evenodd'
										d='M64.4383 12.0339L62.422 11.5626C62.275 11.5287 62.1848 11.3833 62.2194 11.2391L63.875 4.43186C63.9207 4.24163 63.765 4.10107 63.6044 4.10107C63.529 4.10107 63.4524 4.13137 63.3944 4.20286L57.6371 11.2948C57.5135 11.4487 57.5938 11.6777 57.7878 11.7238L59.8041 12.1939C59.9511 12.229 60.0413 12.3732 60.0067 12.5174L58.3524 19.3246C58.3055 19.5149 58.4611 19.6566 58.6218 19.6566C58.6971 19.6566 58.7737 19.6263 58.8318 19.5548L64.5891 12.4617C64.7138 12.3078 64.6335 12.0788 64.4383 12.0339Z'
										fill='white'
									/>
								</svg>
							) : (
								<Text.Heading type='h5' variant='bold'>Sign in to get started</Text.Heading>
							)}
							{success && (
								<Text.Heading
									type='h4'
									mobileL='h5'
									variant='bold'
									style={{
										marginBottom: '12px',
									}}
								>
									Check Your Email
								</Text.Heading>
							)}
							{success ? (
								<Text.Caption
									style={{
										marginBottom: '12px',
									}}
								>
									If an account exists for {email}, a link to access your account will arrive momentarily. Please check your junk mail if you do not receive the link.
									<br />
									<br />
									Not a member yet? Create an account in the TezLab App first to get started.
								</Text.Caption>
							) : (
								<Text.Caption color={styles.colors.lightGray} style={{ marginTop: '6px' }}>
									We'll email you a secure link to subscribe or manage your membership.
								</Text.Caption>
							)}
							{!success && (
								<StyledForm
									{...{ onSubmit }}
								>
									<FormInput
										value={email}
										{...{ onChange }}
										label='TezLab Account Email'
										id='email'
										name='email'
										type='email'
										inputMode='email'
										autoComplete='email'
										spellCheck={false}
										placeholder='you@example.com'
										error={error}
									/>
								</StyledForm>
							)}
							{!!email && (
									success ? (
									<Button
										variant='secondary'
										loading={isSubmitting}
										onClick={onSubmitEmail}
										spinnerVariant='light'
										style={{ marginTop: '12px', alignSelf: 'flex-start' }}
									>
										Resend Link
									</Button>
								) : (
									<Button
										onClick={onSubmitEmail}
										loading={isSubmitting}
										spinnerVariant='light'
										disabled={!validator.isEmail(email)}
											style={{ alignSelf: 'flex-start' }}
									>
										Send Link
									</Button>
								)
							)}
							{!success && (
								<>
									<Text.Caption color={styles.colors.mediumGray} style={{ marginTop: '20px' }}>
										New to TezLab? Create your account in the free app first.
									</Text.Caption>
									<StoreRow>
										<StoreButton type='apple' />
										<StoreButton type='android' />
									</StoreRow>
								</>
							)}
						</InputWrapper>
					</Col>
				</Row>
			</TopContainer>

			<ProofStrip {...{ ratings }} />

			<StyledContainer style={{ marginTop: '0' }}>
				<ValuePanel>
					{VALUE_BLOCKS.map(({ title, body }) => (
						<ValueCell key={title}>
							<Text.Heading type='h5' variant='bold' color={styles.colors.founders} style={{ marginBottom: '8px' }}>{title}</Text.Heading>
							<Text.Body color={styles.colors.lightGray}>{body}</Text.Body>
						</ValueCell>
					))}
				</ValuePanel>
			</StyledContainer>

			<StyledContainer style={{ marginTop: '0' }}>
				<FeatureTableRow.Footer onClick={goToInput} disclaimer={upgrade_features?.disclaimer} key_features={upgrade_features?.key_features} {...pricing} />
			</StyledContainer>
			<StyledContainer id='features' style={{ scrollMarginTop: '88px' }}>
				<Text.Heading type='h3' variant='bold' color={styles.colors.dimHighlight} style={{ textAlign: 'center', marginBottom: '48px' }}>Compare all features</Text.Heading>
				<FeatureTableRow.Heading />
				<FeatureTableRow.Grouped features={upgrade_features?.features || []} />
			</StyledContainer>
			<StyledContainer style={{ marginTop: '0' }} id='gift'>
				<Row>
					<Col className='d-flex'>
						<BottonGiftContainer>
							<BotomGiftIconContainer>
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
									<path d="M9.18555 3.95625L10.6809 6.5H10.625H7.53125C6.58164 6.5 5.8125 5.73086 5.8125 4.78125C5.8125 3.83164 6.58164 3.0625 7.53125 3.0625H7.62578C8.26602 3.0625 8.86328 3.40195 9.18555 3.95625ZM3.75 4.78125C3.75 5.4 3.90039 5.98438 4.1625 6.5H2.375C1.61445 6.5 1 7.11445 1 7.875V10.625C1 11.3855 1.61445 12 2.375 12H21.625C22.3855 12 23 11.3855 23 10.625V7.875C23 7.11445 22.3855 6.5 21.625 6.5H19.8375C20.0996 5.98438 20.25 5.4 20.25 4.78125C20.25 2.69297 18.557 1 16.4688 1H16.3742C15.0035 1 13.7316 1.72617 13.0355 2.90781L12 4.67383L10.9645 2.91211C10.2684 1.72617 8.99648 1 7.62578 1H7.53125C5.44297 1 3.75 2.69297 3.75 4.78125ZM18.1875 4.78125C18.1875 5.73086 17.4184 6.5 16.4688 6.5H13.375H13.3191L14.8145 3.95625C15.141 3.40195 15.734 3.0625 16.3742 3.0625H16.4688C17.4184 3.0625 18.1875 3.83164 18.1875 4.78125ZM2.375 13.375V20.9375C2.375 22.0762 3.29883 23 4.4375 23H10.625V13.375H2.375ZM13.375 23H19.5625C20.7012 23 21.625 22.0762 21.625 20.9375V13.375H13.375V23Z" fill="#2C323B" />
								</svg>
							</BotomGiftIconContainer>
							<BottomGiftTextContainer>
								<Text.Heading type='h3' variant='bold' color={styles.colors.founders}>Gift a Pro Subscription</Text.Heading>
								<Text.Body color={styles.colors.whiteGray}>Give the gift of TezLab Pro to a friend or family member.</Text.Body>
							</BottomGiftTextContainer>
							<Button
								href='/gift'
								as='a'
								style={{
									backgroundColor: styles.colors.founders,
								}}
							>
								<Text.Caption
									color={styles.colors.background}
									variant='bold'
									style={{ marginRight: '12px' }}
								>
									Buy Now
								</Text.Caption>
								<svg
									width='16'
									height='16'
									viewBox='0 0 16 16'
									fill='none'
									xmlns='http://www.w3.org/2000/svg'
								>
									<path
										d='M10.7813 8.66667L8.39464 11.06L9.33463 12L13.3346 8L9.33463 4L8.39464 4.94L10.7813 7.33333H2.66797V8.66667H10.7813Z'
										fill={styles.colors.background}
									/>
								</svg>
							</Button>
						</BottonGiftContainer>
					</Col>
				</Row>
			</StyledContainer>
			<Text.Heading
				type='h2'
				variant='bold'
				style={{ textAlign: 'center', margin: '0 56px 16px 56px' }}
				mobileL='h3'
			>
				Download today and start exploring the EV lifestyle.
			</Text.Heading>
			<CenteredDiv style={{ scrollMarginTop: '88px' }} id='download' style={{ marginBottom: '56px' }}>
				<StoreButton type='apple' />
				<StoreButton type='android' />
			</CenteredDiv>
		</ScreenWrapper>
	)
}
